import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../environments/environment';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { HeaderComponent } from '../../header/header.component';
import { FooterComponent } from '../../footer/footer.component';
import { DeleteDialogComponent } from '../../delete/delete-dialog.component';
import { ResourceAvailabilityDialogComponent } from './resource-availability-dialog.component';

interface Resource {
  id: string;
  name: string;
  resourceTypeId: string;
  [key: string]: any;
}

interface ResourceType {
  id: string;
  name: string;
  [key: string]: any;
}

interface ResourceWeekAvailability {
  id: string;
  resourceId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

@Component({
  selector: 'app-resource-availability',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    MatMenuModule,
    HeaderComponent,
    FooterComponent,
  ],
  templateUrl: './resource-availability.component.html',
  styleUrls: ['./resource-availability.component.scss']
})
export class ResourceAvailabilityComponent implements OnInit {
  resource: Resource | null = null;
  resourceType: ResourceType | null = null;
  availabilities: ResourceWeekAvailability[] = [];
  loading = false;
  error: string | null = null;
  
  // Days of the week for display
  daysOfWeek = [
    { value: 1, name: 'Monday' },
    { value: 2, name: 'Tuesday' },
    { value: 3, name: 'Wednesday' },
    { value: 4, name: 'Thursday' },
    { value: 5, name: 'Friday' },
    { value: 6, name: 'Saturday' },
    { value: 7, name: 'Sunday' }
  ];
  
  private apiBaseUrl = environment.apiBaseUrl;
  private organisationId: string = '';
  private resourceId: string = '';
  private resourceTypeId: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    public dialog: MatDialog
  ) {}

  ngOnInit() {
    // Get selected organisationId from localStorage
    const storedId = localStorage.getItem('selectedOrganisationId');
    if (!storedId) {
      this.error = 'No organisation selected.';
      return;
    }
    this.organisationId = storedId;
    
    // Get resource ID from route params
    this.route.params.subscribe(params => {
      this.resourceTypeId = params['resourceTypeId'];
      this.resourceId = params['resourceId'];
      
      if (this.resourceId && this.resourceTypeId) {
        this.fetchResourceDetails();
        this.fetchAvailabilities();
      } else {
        this.error = 'Invalid resource or resource type ID.';
      }
    });
  }

  fetchResourceDetails() {
    this.loading = true;
    this.http.get<Resource>(
      `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.resourceTypeId}/resources/${this.resourceId}`
    ).subscribe({
      next: (data) => {
        this.resource = data;
        this.fetchResourceType();
      },
      error: (err: HttpErrorResponse) => {
        this.error = err.message;
        this.loading = false;
        this.snackBar.open('Failed to load resource details', 'Close', {
          duration: 3000,
        });
      }
    });
  }

  fetchResourceType() {
    this.http.get<ResourceType>(
      `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.resourceTypeId}`
    ).subscribe({
      next: (data) => {
        this.resourceType = data;
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.error = err.message;
        this.loading = false;
        this.snackBar.open('Failed to load resource type details', 'Close', {
          duration: 3000,
        });
      }
    });
  }

  fetchAvailabilities() {
    this.loading = true;
    this.http.get<ResourceWeekAvailability[]>(
      `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.resourceTypeId}/resources/${this.resourceId}/availabilities`
    ).subscribe({
      next: (data) => {
        this.availabilities = data;
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.error = err.message;
        this.loading = false;
        this.snackBar.open('Failed to load availabilities', 'Close', {
          duration: 3000,
        });
      }
    });
  }

  getDayName(dayOfWeek: number): string {
    const day = this.daysOfWeek.find(d => d.value === dayOfWeek);
    return day ? day.name : 'Unknown';
  }

  formatTime(time: string): string {
    return time;
  }
  
  getAvailabilitiesForDay(dayOfWeek: number): ResourceWeekAvailability[] {
    return this.availabilities.filter(a => a.dayOfWeek === dayOfWeek);
  }

  openAddDialog() {
    const dialogRef = this.dialog.open(ResourceAvailabilityDialogComponent, {
      width: '400px',
      data: {
        resourceId: this.resourceId,
        dayOfWeek: 1,
        startTime: '09:00',
        endTime: '17:00',
        daysOfWeek: this.daysOfWeek,
        availabilities: this.availabilities
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.createAvailability(result);
      }
    });
  }

  openEditDialog(availability: ResourceWeekAvailability) {
    const dialogRef = this.dialog.open(ResourceAvailabilityDialogComponent, {
      width: '400px',
      data: {
        ...availability,
        daysOfWeek: this.daysOfWeek,
        availabilities: this.availabilities.filter(a => a.id !== availability.id)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updateAvailability(availability.id, result);
      }
    });
  }

  deleteAvailability(availability: ResourceWeekAvailability) {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '350px',
      data: {
        title: 'Delete Availability',
        message: `Are you sure you want to delete this availability slot (${this.getDayName(availability.dayOfWeek)} ${availability.startTime} - ${availability.endTime})?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.http.delete(
          `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.resourceTypeId}/resources/${this.resourceId}/availabilities/${availability.id}`
        ).subscribe({
          next: () => {
            this.snackBar.open('Availability deleted', 'Close', {
              duration: 2000,
            });
            this.fetchAvailabilities();
          },
          error: (err: HttpErrorResponse) => {
            this.error = err.error?.error || err.message;
            this.snackBar.open('Failed to delete availability', 'Close', {
              duration: 3000,
            });
          }
        });
      }
    });
  }

  createAvailability(availability: Partial<ResourceWeekAvailability>) {
    this.http.post<ResourceWeekAvailability>(
      `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.resourceTypeId}/resources/${this.resourceId}/availabilities`,
      {
        resourceId: parseInt(this.resourceId),
        dayOfWeek: availability.dayOfWeek,
        startTime: availability.startTime,
        endTime: availability.endTime
      }
    ).subscribe({
      next: () => {
        this.snackBar.open('Availability created', 'Close', {
          duration: 2000,
        });
        this.fetchAvailabilities();
      },
      error: (err: HttpErrorResponse) => {
        this.error = err.error?.error || err.message;
        this.snackBar.open('Failed to create availability', 'Close', {
          duration: 3000,
        });
      }
    });
  }

  updateAvailability(id: string, availability: Partial<ResourceWeekAvailability>) {
    this.http.put<ResourceWeekAvailability>(
      `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.resourceTypeId}/resources/${this.resourceId}/availabilities/${id}`,
      {
        resourceId: parseInt(this.resourceId),
        dayOfWeek: availability.dayOfWeek,
        startTime: availability.startTime,
        endTime: availability.endTime
      }
    ).subscribe({
      next: () => {
        this.snackBar.open('Availability updated', 'Close', {
          duration: 2000,
        });
        this.fetchAvailabilities();
      },
      error: (err: HttpErrorResponse) => {
        this.error = err.error?.error || err.message;
        this.snackBar.open('Failed to update availability', 'Close', {
          duration: 3000,
        });
      }
    });
  }

  copyToAllDays(sourceSlot: ResourceWeekAvailability, weekdaysOnly: boolean) {
    // Get all days except the source day
    let targetDays = this.daysOfWeek.filter(day => day.value !== sourceSlot.dayOfWeek);
    
    // Filter out weekends if weekdaysOnly is true
    if (weekdaysOnly) {
      targetDays = targetDays.filter(day => day.value < 6); // Filter out Saturday (6) and Sunday (7)
    }
    
    // Check for overlaps on each day
    const overlappingDays: number[] = [];
    
    for (const day of targetDays) {
      const daySlots = this.getAvailabilitiesForDay(day.value);
      
      for (const slot of daySlots) {
        if (
          (sourceSlot.startTime >= slot.startTime && sourceSlot.startTime < slot.endTime) ||
          (sourceSlot.endTime > slot.startTime && sourceSlot.endTime <= slot.endTime) ||
          (sourceSlot.startTime <= slot.startTime && sourceSlot.endTime >= slot.endTime)
        ) {
          overlappingDays.push(day.value);
          break;
        }
      }
    }
    
    if (overlappingDays.length > 0) {
      // Show warning about overlapping days
      const dayNames = overlappingDays
        .map(dayValue => this.getDayName(dayValue))
        .join(', ');
      
      this.snackBar.open(`Cannot copy to ${dayNames} due to overlapping slots`, 'Close', {
        duration: 5000,
      });
      
      // Filter out days with overlaps
      const availableDays = targetDays.filter(day => !overlappingDays.includes(day.value));
      
      if (availableDays.length === 0) {
        return;
      }
      
      // Confirm copying to available days
      const dialogRef = this.dialog.open(DeleteDialogComponent, {
        width: '400px',
        data: {
          title: 'Copy Time Slot',
          message: `Copy this time slot (${sourceSlot.startTime} - ${sourceSlot.endTime}) to all available days?`,
          confirmText: 'Copy',
          cancelText: 'Cancel',
        },
      });
      
      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          this.createCopiesForDays(sourceSlot, availableDays.map(day => day.value));
        }
      });
    } else {
      // No overlaps, confirm copying to all days
      const dialogRef = this.dialog.open(DeleteDialogComponent, {
        width: '400px',
        data: {
          title: 'Copy Time Slot',
          message: `Copy this time slot (${sourceSlot.startTime} - ${sourceSlot.endTime}) to ${weekdaysOnly ? 'all weekdays' : 'all other days'}?`,
          confirmText: 'Copy',
          cancelText: 'Cancel',
        },
      });
      
      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          this.createCopiesForDays(sourceSlot, targetDays.map(day => day.value));
        }
      });
    }
  }
  
  createCopiesForDays(sourceSlot: ResourceWeekAvailability, targetDays: number[]) {
    let completedCount = 0;
    let errorCount = 0;
    
    for (const dayOfWeek of targetDays) {
      this.http.post<ResourceWeekAvailability>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.resourceTypeId}/resources/${this.resourceId}/availabilities`,
        {
          resourceId: parseInt(this.resourceId),
          dayOfWeek: dayOfWeek,
          startTime: sourceSlot.startTime,
          endTime: sourceSlot.endTime
        }
      ).subscribe({
        next: () => {
          completedCount++;
          if (completedCount + errorCount === targetDays.length) {
            this.snackBar.open(`Time slot copied to ${completedCount} days`, 'Close', {
              duration: 3000,
            });
            this.fetchAvailabilities();
          }
        },
        error: (err: HttpErrorResponse) => {
          errorCount++;
          console.error('Error copying time slot:', err);
          if (completedCount + errorCount === targetDays.length) {
            if (completedCount > 0) {
              this.snackBar.open(`Time slot copied to ${completedCount} days (${errorCount} failed)`, 'Close', {
                duration: 3000,
              });
              this.fetchAvailabilities();
            } else {
              this.snackBar.open('Failed to copy time slots', 'Close', {
                duration: 3000,
              });
            }
          }
        }
      });
    }
  }

  goBack() {
    this.router.navigate(['/resources']);
  }
}
